import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DateTime, Dropdown, Icon, Tab, Table, Tabs } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import SyncLogDrawer from './SyncLogDrawer';
import { useParams } from 'react-router-dom';

const REFRESH_DELAY_SECONDS = 5;
const REFRESH_IMPORT_STATUS_SECONDS = 10;

const Message = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  color: ${colors.grey55};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  font-weight: ${weights.light};
`;

const Actions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: stretch;
  border: 1px solid ${colors.grey10};
  border-radius: 0.3125rem;

  > * + * {
    border-left: 1px solid ${colors.grey10};
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  padding: 0.375rem;
  background: transparent;
  border: none;
  color: ${colors.primary};
  cursor: pointer;
  transition: all 150ms ease-in-out;

  .icon {
    font-size: 0.75rem;
  }

  &:hover:not(:disabled) {
    background-color: ${colors.primary};
    color: ${colors.white};
  }

  &:disabled {
    color: ${colors.grey25};
    cursor: not-allowed;
  }

  &:first-child {
    border-radius: 0.3125rem 0 0 0.3125rem;
  }

  &:last-child {
    border-radius: 0 0.3125rem 0.3125rem 0;
  }
`;

const TabWrapper = styled.div`
  margin-top: 1rem;
`;

const TableWrapper = styled.div`
  margin-top: 0.5rem;
`;

export default function SyncResults({ refreshDate, onSyncNow }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const { sageConnectionId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [syncs, setSyncs] = useState([]);
  const [vendorBillEnabled, setVendorBillEnabled] = useState(false);
  const [viewSync, setViewSync] = useState();
  const [tabIndex, setTabIndex] = useState(0);
  const [canRefresh, setCanRefresh] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getConfiguration();

      if (!isMounted.current) return;
      setVendorBillEnabled(data.vendorBillEnabled);
    })();
  }, [api, workspace.id, sageConnectionId, isMounted]);

  const fetchSyncs = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getSyncs();
      const hasAutoSyncs = _.filter(data, { typeId: 'auto' }).length > 0;
      const hasManualSyncs = _.filter(data, { typeId: 'manual' }).length > 0;

      setTabIndex((currentIndex) => {
        if (!hasAutoSyncs && currentIndex === 0) {
          return 1;
        } else if (!hasManualSyncs && currentIndex === 1) {
          return 0;
        } else {
          return currentIndex;
        }
      });
      setSyncs(data);
    } finally {
      setIsLoading(false);
    }
  }, [api, workspace, sageConnectionId]);

  const isRunning = useMemo(() => {
    return _.filter(syncs, { isRunning: true }).length > 0;
  }, [syncs]);

  const autoSyncs = useMemo(() => {
    return _.filter(syncs, { typeId: 'auto' });
  }, [syncs]);

  const manualSyncs = useMemo(() => {
    return _.filter(syncs, { typeId: 'manual' });
  }, [syncs]);

  const currentSyncs = useMemo(() => {
    return tabIndex === 0 ? autoSyncs : manualSyncs;
  }, [autoSyncs, manualSyncs, tabIndex]);

  useEffect(() => {
    fetchSyncs();
  }, [fetchSyncs, refreshDate]);

  useEffect(() => {
    let interval = null;
    if (isRunning) {
      interval = setInterval(fetchSyncs, REFRESH_IMPORT_STATUS_SECONDS * 1000);
    }
    return () => {
      if (interval) {
        clearTimeout(interval);
      }
    };
  }, [isRunning, fetchSyncs]);

  const refresh = () => {
    if (!canRefresh) {
      return;
    }
    setCanRefresh(false);
    fetchSyncs();
    setTimeout(() => {
      if (isMounted.current) {
        setCanRefresh(true);
      }
    }, REFRESH_DELAY_SECONDS * 1000);
  };

  const actions = (
    <Actions>
      <ActionButton disabled={!canRefresh} onClick={refresh} title="Refresh results">
        <Icon icon="sync" />
      </ActionButton>

      {onSyncNow && (
        <Dropdown align="right">
          {({ setIsOpen }) => (
            <>
              <Dropdown.Trigger>
                <ActionButton onClick={() => setIsOpen(true)}>
                  <Icon icon="chevron-down" />
                </ActionButton>
              </Dropdown.Trigger>
              <Dropdown.Menu>
                <Dropdown.Item
                  disabled={isRunning || !vendorBillEnabled}
                  tooltip={
                    !vendorBillEnabled
                      ? 'The reimbursable expenses sync within the Sage Intacct integration configuration is not enabled.'
                      : undefined
                  }
                  onClick={() => {
                    setIsOpen(false);
                    onSyncNow();
                  }}>
                  Sync Expenses Now
                </Dropdown.Item>
              </Dropdown.Menu>
            </>
          )}
        </Dropdown>
      )}
    </Actions>
  );

  if (isLoading) {
    return <PageLoader />;
  }
  if (syncs.length === 0) {
    return (
      <>
        <TitleRow>
          <Title>Latest Sync Results</Title>
          {actions}
        </TitleRow>
        <TableWrapper>
          <Message>No results available</Message>
        </TableWrapper>
      </>
    );
  }
  return (
    <>
      <TitleRow>
        <Title>Latest Sync Results</Title>
        {actions}
      </TitleRow>
      <TabWrapper>
        <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
          {autoSyncs.length > 0 && <Tab>Automatic</Tab>}
          {manualSyncs.length > 0 && <Tab>Manual</Tab>}
        </Tabs>
      </TabWrapper>
      <TableWrapper>
        <Table small>
          <Table.BoxHeader>
            <Table.Column>Date</Table.Column>
            <Table.Column>Status</Table.Column>
            <Table.Column width="9rem">Type</Table.Column>
            <Table.Column width="9rem">Duration</Table.Column>
            <Table.Column width="9rem">Changes</Table.Column>
          </Table.BoxHeader>
          <Table.Body>
            {currentSyncs.map((sync) => (
              <Table.BoxRow
                key={sync.id}
                isDisabled={!sync.hasLog}
                onClick={() => (sync.hasLog ? setViewSync(sync.id) : null)}>
                <Table.Cell>
                  <DateTime value={sync.createdAt} format="LLL" />
                </Table.Cell>
                <Table.Cell>{sync.status?.name || 'N/A'}</Table.Cell>
                <Table.Cell>{sync.type?.name || 'N/A'}</Table.Cell>
                <Table.Cell>{formatDuration(sync.startedAt, sync.endedAt)}</Table.Cell>
                <Table.Cell>{sync.changeCount ?? 'N/A'}</Table.Cell>
              </Table.BoxRow>
            ))}
          </Table.Body>
        </Table>
      </TableWrapper>
      {viewSync && <SyncLogDrawer id={viewSync} onClose={() => setViewSync()} />}
    </>
  );
}

function formatDuration(startedAt, endedAt) {
  if (!startedAt || !endedAt) return 'N/A';

  const start = new Date(startedAt);
  const end = new Date(endedAt);
  const diff = end - start;

  const milliseconds = diff % 1000;
  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);

  let readable = '';
  if (hours > 0) readable += `${hours}h `;
  if (minutes > 0) readable += `${minutes}m `;

  if (diff < 1000) {
    readable += `${milliseconds}ms`;
  } else {
    if (seconds > 0) readable += `${seconds}s`;
  }

  return readable.trim();
}
